import { DiscountValueType } from '../types/api';

/**
 * Расчёт цены по ADR-026 §3 — те же правила, что на сервере и в приложении:
 *
 * - процент: `floor(price × (100 − p) / 100)` — вниз, как `MenuItemPricer`;
 * - тенге: `max(0, price − amount)`;
 * - комбо: цена набора — `bundle_price`, обычная — Σ цена × количество.
 *
 * Кабинет считает цену только для превью: что увидит гость, решает сервер.
 * Считать здесь иначе нельзя — партнёр увидит одну цену, гость другую.
 */

/** Цена позиции со скидкой. */
export function discountedPrice(price: number, valueType: DiscountValueType, value: number): number {
  if (valueType === 'amount') return Math.max(0, price - value);

  return Math.max(0, Math.floor((price * (100 - value)) / 100));
}

/** Позиция с ценой — всё, что нужно расчёту. */
export interface PricedItem {
  id: number;
  name: string;
  price: number;
}

export interface PriceExtremes<T extends PricedItem> {
  cheapest: T;
  priciest: T;
}

/**
 * Самая дешёвая и самая дорогая позиции. При равных ценах берётся первая по
 * порядку меню — превью не прыгает между одинаковыми позициями.
 */
export function priceExtremes<T extends PricedItem>(items: T[]): PriceExtremes<T> | null {
  if (items.length === 0) return null;

  return items.reduce<PriceExtremes<T>>(
    (acc, item) => ({
      cheapest: item.price < acc.cheapest.price ? item : acc.cheapest,
      priciest: item.price > acc.priciest.price ? item : acc.priciest,
    }),
    { cheapest: items[0], priciest: items[0] },
  );
}

/** Строка состава комбо. */
export interface PricedLine {
  price: number;
  quantity: number;
}

/** Обычная цена набора: Σ цена × количество. */
export function comboRegularPrice(lines: PricedLine[]): number {
  return lines.reduce((sum, line) => sum + line.price * line.quantity, 0);
}

/** Сколько штук в наборе — «две позиции или одна дважды». */
export function comboPieces(lines: Array<{ quantity: number }>): number {
  return lines.reduce((sum, line) => sum + line.quantity, 0);
}

export interface ComboSaving {
  /** ₸, всегда больше нуля. */
  amount: number;
  /** Выгода в процентах от обычной цены, 1–100 — как эквивалент на сервере. */
  percent: number;
}

/**
 * Выгода гостя от комбо. null — выгоды нет: такое комбо сервер не сохранит,
 * а гость, если цены в меню снизили позже, его не увидит.
 */
export function comboSaving(regularPrice: number, bundlePrice: number): ComboSaving | null {
  const amount = regularPrice - bundlePrice;
  if (regularPrice <= 0 || amount <= 0) return null;

  const percent = Math.min(100, Math.max(1, Math.round((amount * 100) / regularPrice)));

  return { amount, percent };
}
